import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Col, Modal, Row } from 'react-bootstrap'
import { Button, Stepper } from '@components/common'
import { CampaignInfo, Triggers, Scheduling, Review } from './Subcomponents'
import {
  CampaignMessageStatus,
  CampaignTemplateChannel,
  ICampaignMessage,
  ICampaignRecipient,
  ICampaignRequest,
  ICampaignTriggerKeyword,
  ICampaignTriggerMessage,
} from '@dataTypes/marketing.type'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import './AddCampaignStyle.css'
import '@components/pages/Marketing/Template/MarketingEmailTemplate.css'
import {
  addCampaign,
  bulkAddCampaignRecipient,
  bulkAddKeywords,
  bulkAddTriggerMessages,
  bulkDeleteCampaignRecipient,
  bulkDeleteKeywords,
  bulkDeleteTriggerMessages,
  bulkUpdateKeywords,
  bulkUpdateTriggerMessages,
  fetchAllCampaigns,
  fetchAllContacts,
  scheduleSendSms,
  scheduleSendEmail,
  sendCampaignSms,
  sendCampaignEmail,
  updateCampaign,
} from '@store/actions'
import { useGlobalLoadingActions } from '@hooks'
import {
  analyzeKeywords,
  analyzeRecipients,
  analyzeTimePoints,
  generateCampaignMessages,
  generateTriggerMessages,
} from '@utils/marketing.utils'
import { toPersistedCampaignTemplateId } from '@utils/campaign.util'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  isEmailBackendEnabled,
  requestGoogleWorkspaceStatus,
} from '@services/api/email.api'
import { getSettingsRouteById } from '@pages/Settings/settingsRoutes'

declare global {
  interface Window {
    bootstrap: any
  }
}

interface AddCampaignProps {
  initialCampaign?: ICampaignRequest
  show: boolean
  onHide: () => void
  onSave?: (campaignDetails: ICampaignRequest) => void
  method?: string
}
const defaultCampaign: ICampaignRequest = {
  name: '',
  channel: CampaignTemplateChannel.sms,
  message_status: 'Draft',
  campaign_status: 'Inactive',
  campaign_template_id: null,
  clinic_id: '',
  created_by: '',
  updated_by: '',
  location_filter: [],
  patient_status_filter: [],
  age_range_filter: [],
  appt_status_filter: [],
  recall_type_filter: [],
  celebration_type_filter: [],
  campaign_trigger_keyword: [],
  campaign_trigger_message: [],
  campaign_recipient: [],
  campaign_message: [],
}

const AddCampaignModal: React.FC<AddCampaignProps> = ({
  initialCampaign,
  show,
  onHide,
  onSave,
  method,
}) => {
  const dispatch = useAppDispatch()
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const [showTimePointModal, setShowTimePointModal] = useState(false)
  const [showActionTriggerModal, setShowActionTriggerModal] = useState(false)
  const [previewMessage, setPreviewMessage] = useState<string | null>(null)
  const [newCampaign, setNewCampaign] = useState<ICampaignRequest>(
    initialCampaign ?? defaultCampaign,
  )
  const [isDraft, setIsDraft] = useState(false)
  const [step, setStep] = useState(0)
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const [googleMailboxConnected, setGoogleMailboxConnected] = useState(true)
  const [googleMailboxLoaded, setGoogleMailboxLoaded] = useState(false)

  const steps = ['Campaign info', 'Scheduling', 'Trigger events', 'Review']
  const { loading, error } = useAppSelector((state) => state.marketing)
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const emailSettingsPath =
    getSettingsRouteById('comm-email')?.fullPath ??
    '/settings/communications/email'
  const emailSendBlocked =
    newCampaign.channel === CampaignTemplateChannel.email &&
    googleMailboxLoaded &&
    !googleMailboxConnected

  useEffect(() => {
    if (!show) return
    if (!isEmailBackendEnabled()) {
      setGoogleMailboxConnected(false)
      setGoogleMailboxLoaded(true)
      return
    }
    setGoogleMailboxLoaded(false)
    void requestGoogleWorkspaceStatus()
      .then((status) => {
        setGoogleMailboxConnected(Boolean(status?.isConnected))
      })
      .catch(() => {
        setGoogleMailboxConnected(false)
      })
      .finally(() => {
        setGoogleMailboxLoaded(true)
      })
  }, [show, selectedClinic?.clinic_id])

  useEffect(() => {
    if (
      googleMailboxLoaded &&
      !googleMailboxConnected &&
      newCampaign.channel === CampaignTemplateChannel.email
    ) {
      setNewCampaign((prev) => ({
        ...prev,
        channel: CampaignTemplateChannel.sms,
      }))
    }
  }, [googleMailboxLoaded, googleMailboxConnected])

  const campaignSubmitMetaRef = useRef<{
    isDraft: boolean
    isReactivate: boolean
    isEdit: boolean
    smsWarning?: string
  } | null>(null)
  const sawCampaignSubmitLoadingRef = useRef(false)

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {}
    switch (step) {
      case 0:
        if (!newCampaign.name || newCampaign.name.trim() === '') {
          errors.name = 'Input campaign name'
        }
        if (!newCampaign.type || (newCampaign.type as string) === '') {
          errors.type = 'Select campaign type'
        }
        if (!newCampaign.channel || newCampaign.channel === '') {
          errors.channel = 'Select a channel'
        }
        if (
          newCampaign.channel === CampaignTemplateChannel.email &&
          (!newCampaign.subject || newCampaign.subject.trim() === '')
        ) {
          errors.subject = 'Input email subject'
        }
        if (
          !newCampaign.text_content ||
          newCampaign.text_content.trim().length === 0
        ) {
          errors.text_content = 'Input campaign message'
        }
        break
      case 1:
        if (
          newCampaign.channel === CampaignTemplateChannel.email &&
          newCampaign.campaign_recipient.some((r) => !r.contact?.email?.trim())
        ) {
          errors.campaign_recipient =
            'Remove recipients without an email address'
        }
        if (!newCampaign.timezone || newCampaign.timezone === '') {
          errors.timezone = 'Select timezone'
        }
        if (!newCampaign.start_date || newCampaign.start_date === '') {
          errors.start_date = 'Select start date'
        }
        if (!newCampaign.start_time || newCampaign.start_time === '') {
          errors.start_time = 'Select start time'
        }
        if (!newCampaign.end_date || newCampaign.end_date === '') {
          errors.end_date = 'Select end date'
        }
        if (!newCampaign.end_time || newCampaign.end_time === '') {
          errors.end_time = 'Select end time'
        }
        if (
          newCampaign.end_date &&
          newCampaign.start_date &&
          newCampaign.start_time &&
          newCampaign.end_time
        ) {
          const startDate = new Date(newCampaign.start_date)
          const [startHours, startMinutes, startSeconds] =
            newCampaign.start_time.split(':').map(Number)
          startDate.setHours(startHours, startMinutes, startSeconds, 0)
          const endDate = new Date(newCampaign.end_date)
          const [endHours, endMinutes, endSeconds] = newCampaign.end_time
            .split(':')
            .map(Number)
          endDate.setHours(endHours, endMinutes, endSeconds, 0)
          if (startDate.getTime() >= endDate.getTime()) {
            errors.start_date = 'Start date cannot be later than end date.'
            errors.end_date = 'Select an end date after the start date.'
          }
        }
        if (!newCampaign.sending_option || newCampaign.sending_option === '') {
          errors.sending_option = 'Select sending option'
        }
        if (newCampaign.sending_option === 'Later') {
          if (!newCampaign.schedule_date || newCampaign.schedule_date === '') {
            errors.schedule_date = 'Select schedule date'
          }
          if (!newCampaign.schedule_time || newCampaign.schedule_time === '') {
            errors.schedule_time = 'Select schedule time'
          }
          if (newCampaign.schedule_date && newCampaign.schedule_time) {
            const scheduleDate = new Date(newCampaign.schedule_date)
            const [hours, minutes, seconds] = newCampaign.schedule_time
              .split(':')
              .map(Number)
            scheduleDate.setHours(hours, minutes, seconds, 0)
            const now = new Date()
            if (scheduleDate.getTime() <= now.getTime()) {
              errors.schedule_time = 'Schedule time cannot be in the past.'
            }
          }
        } else if (newCampaign.sending_option === 'Recurring') {
          if (
            !newCampaign.recurring_interval ||
            newCampaign.recurring_interval === ''
          ) {
            errors.recurring_interval = 'Select interval'
          }
          if (
            newCampaign.recurring_interval === 'Weekly' &&
            (!newCampaign.recurring_days || newCampaign.recurring_days === '')
          ) {
            errors.recurring_days = 'Select recurring days'
          }
          if (
            !newCampaign.recurring_time ||
            newCampaign.recurring_time === ''
          ) {
            errors.recurring_time = 'Select recurring time'
          }
          if (
            !newCampaign.recurring_duration ||
            newCampaign.recurring_duration <= 0
          ) {
            errors.recurring_duration = 'Select recurring duration'
          }
        }
        break
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleNext = (): void => {
    if (validateForm()) setStep(step + 1)
  }

  const handlePrevious = (): void => setStep(step - 1)

  const handleEditButton = (): void => setStep(0)

  const onModalClose = (): void => {
    setStep(0)
    setValidationErrors({})
    setNewCampaign(defaultCampaign)
    onHide()
  }

  useLayoutEffect(() => {
    if (!show) {
      hideLoading('#add-campaign-modal .modal-content')
      campaignSubmitMetaRef.current = null
      sawCampaignSubmitLoadingRef.current = false
      return
    }

    if (step !== 4) {
      campaignSubmitMetaRef.current = null
      sawCampaignSubmitLoadingRef.current = false
      return
    }

    if (!campaignSubmitMetaRef.current) {
      campaignSubmitMetaRef.current = {
        isDraft,
        isReactivate: method === 'reactivate',
        isEdit: !!initialCampaign?.campaign_id,
      }
    }

    const isSubmitLoading = loading.add || loading.delete || loading.update

    if (isSubmitLoading) {
      showLoading({ container: '#add-campaign-modal .modal-content' })
      sawCampaignSubmitLoadingRef.current = true
      return
    }

    if (!sawCampaignSubmitLoadingRef.current) {
      return
    }

    hideLoading('#add-campaign-modal .modal-content')
    sawCampaignSubmitLoadingRef.current = false

    const errMsg = error.add || error.delete || error.update
    const meta = campaignSubmitMetaRef.current
    campaignSubmitMetaRef.current = null

    if (errMsg) {
      const detail = (errMsg as string)?.trim()
      showToast(
        createFailedToast(
          'Error',
          detail || 'Campaign could not be saved. Please try again.',
        ),
      )
      onModalClose()
      return
    }

    let message: string
    if (meta?.isReactivate) {
      message = 'Campaign reactivated successfully.'
    } else if (meta?.isEdit) {
      message = 'Campaign updated successfully.'
    } else {
      message = 'Campaign created successfully.'
    }
    if (meta?.isDraft) {
      message = message.replace(/\.$/, '') + ' as draft.'
    }
    if (meta?.smsWarning) {
      message = `${message.replace(/\.$/, '')}. ${meta.smsWarning}`
    }

    showToast(createSuccessToast('Success', message))
    onModalClose()
  }, [
    show,
    step,
    loading.add,
    loading.delete,
    loading.update,
    error.add,
    error.delete,
    error.update,
    isDraft,
    method,
    initialCampaign?.campaign_id,
    showToast,
    onModalClose,
    dispatch,
    showLoading,
    hideLoading,
  ])

  const shouldSendOutboundMessages = (
    formData: ICampaignRequest,
    isNewCampaign: boolean,
  ): boolean => {
    if (
      formData.message_status === 'Draft' ||
      formData.campaign_status === 'Inactive'
    ) {
      return false
    }
    if (isNewCampaign) {
      return true
    }
    if (method === 'reactivate') {
      return true
    }
    if (initialCampaign?.message_status === 'Draft') {
      return true
    }
    return false
  }

  const recordSmsSendOutcome = (result: ICampaignMessage[]): void => {
    const failed = result.filter(
      (message) => message.status === CampaignMessageStatus.failed,
    ).length
    if (failed > 0 && campaignSubmitMetaRef.current) {
      campaignSubmitMetaRef.current.smsWarning = `${result.length - failed} of ${result.length} messages sent. ${failed} failed.`
    }
  }

  const handleSendCampaignMessage = async (
    data: ICampaignRequest,
  ): Promise<void> => {
    if (!data.campaign_id) {
      return
    }
    if (
      data.message_status === 'Draft' ||
      data.campaign_status === 'Inactive'
    ) {
      return
    }
    const isEmail = data.channel === CampaignTemplateChannel.email
    const isSms = data.channel === CampaignTemplateChannel.sms
    if (!isEmail && !isSms) {
      return
    }

    if (data.sending_option === 'Immediately') {
      const campaignMessages = generateCampaignMessages(data, selectedClinic)
      campaignMessages.push(...generateTriggerMessages(data, selectedClinic))
      if (campaignMessages.length > 0) {
        const result = await dispatch(
          isEmail
            ? sendCampaignEmail(campaignMessages)
            : sendCampaignSms(campaignMessages),
        ).unwrap()
        if (isSms) {
          recordSmsSendOutcome(result)
        }
      }
    } else if (data.sending_option === 'Later' && data.schedule_date) {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      today.setHours(0, 0, 0, 0)
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      tomorrow.setHours(23, 59, 59, 999)
      const scheduleDate = new Date(data.schedule_date)
      const scheduleDateOnly = new Date(
        scheduleDate.getFullYear(),
        scheduleDate.getMonth(),
        scheduleDate.getDate(),
      )

      // Send if scheduled for today or tomorrow
      if (
        scheduleDateOnly.getTime() >= today.getTime() ||
        scheduleDateOnly.getTime() <= tomorrow.getTime()
      ) {
        const campaignMessages = generateCampaignMessages(data, selectedClinic)
        campaignMessages.push(...generateTriggerMessages(data, selectedClinic))
        if (campaignMessages.length > 0) {
          const result = await dispatch(
            isEmail
              ? scheduleSendEmail(campaignMessages)
              : scheduleSendSms(campaignMessages),
          ).unwrap()
          if (isSms) {
            recordSmsSendOutcome(result)
          }
        }
      }
    }
  }

  const handleSave = async (_formData: ICampaignRequest): Promise<void> => {
    const formData = {
      ..._formData,
      clinic_id: selectedClinic?.clinic_id,
    }
    if (onSave) {
      onSave(formData)
    }
    let shouldRefreshCampaigns = false
    try {
      if (formData.campaign_id) {
        shouldRefreshCampaigns = true
        await dispatch(
          updateCampaign({ campaign_id: formData.campaign_id, data: formData }),
        ).unwrap()

        const { added: addedRecipients, deleted: deletedRecipients } =
          analyzeRecipients(
            formData.campaign_recipient,
            initialCampaign?.campaign_recipient,
            formData.campaign_id,
          )
        if (deletedRecipients.length > 0) {
          await dispatch(
            bulkDeleteCampaignRecipient(deletedRecipients),
          ).unwrap()
        }
        if (addedRecipients.length > 0) {
          await dispatch(bulkAddCampaignRecipient(addedRecipients)).unwrap()
        }

        const {
          added: addedKeywords,
          deleted: deletedKeywords,
          updated: updatedKeywords,
        } = analyzeKeywords(
          formData.campaign_id,
          formData.campaign_trigger_keyword,
          initialCampaign?.campaign_trigger_keyword,
        )
        if (deletedKeywords.length > 0) {
          await dispatch(bulkDeleteKeywords(deletedKeywords)).unwrap()
        }
        if (updatedKeywords.length > 0) {
          await dispatch(bulkUpdateKeywords(updatedKeywords)).unwrap()
        }
        if (addedKeywords.length > 0) {
          await dispatch(bulkAddKeywords(addedKeywords)).unwrap()
        }

        const {
          added: addedMessages,
          deleted: deletedMessages,
          updated: updatedMessages,
        } = analyzeTimePoints(
          formData.campaign_id,
          formData.campaign_trigger_message,
          initialCampaign?.campaign_trigger_message,
        )
        if (deletedMessages.length > 0) {
          await dispatch(bulkDeleteTriggerMessages(deletedMessages)).unwrap()
        }
        if (updatedMessages.length > 0) {
          await dispatch(bulkUpdateTriggerMessages(updatedMessages)).unwrap()
        }
        if (addedMessages.length > 0) {
          await dispatch(bulkAddTriggerMessages(addedMessages)).unwrap()
        }

        if (shouldSendOutboundMessages(formData, false)) {
          await handleSendCampaignMessage(formData)
        }
      } else {
        const campaign = await dispatch(addCampaign(formData)).unwrap()
        shouldRefreshCampaigns = true
        const campaignId = campaign.campaign_id
        if (!campaignId) {
          showToast(
            createFailedToast(
              'Error',
              'Campaign was created but no campaign ID was returned.',
            ),
          )
          return
        }

        const updatedData: ICampaignRequest = {
          ...formData,
          campaign_id: campaignId,
          campaign_recipient: formData.campaign_recipient.map((r) => ({
            ...r,
            campaign_id: campaignId,
          })),
          campaign_trigger_keyword: formData.campaign_trigger_keyword.map(
            (k) => ({
              ...k,
              campaign_id: campaignId,
              campaign_trigger_keyword_id: undefined,
            }),
          ),
          campaign_trigger_message: formData.campaign_trigger_message.map(
            (m) => ({
              ...m,
              campaign_id: campaignId,
              campaign_trigger_message_id: undefined,
            }),
          ),
        }
        setNewCampaign(updatedData)

        if (updatedData.campaign_recipient.length > 0) {
          await dispatch(
            bulkAddCampaignRecipient(updatedData.campaign_recipient),
          ).unwrap()
        }
        if (updatedData.campaign_trigger_keyword.length > 0) {
          await dispatch(
            bulkAddKeywords(updatedData.campaign_trigger_keyword),
          ).unwrap()
        }
        if (updatedData.campaign_trigger_message.length > 0) {
          await dispatch(
            bulkAddTriggerMessages(updatedData.campaign_trigger_message),
          ).unwrap()
        }
        if (shouldSendOutboundMessages(updatedData, true)) {
          await handleSendCampaignMessage(updatedData)
        }
      }
    } catch {
      // Errors are surfaced via marketing reducer + step 4 useLayoutEffect
    } finally {
      if (shouldRefreshCampaigns) {
        await dispatch(fetchAllCampaigns())
      }
    }
  }

  const handleSaveDraft = (): void => {
    setIsDraft(true)
    handleSave({
      ...newCampaign,
      campaign_template_id: toPersistedCampaignTemplateId(
        newCampaign.campaign_template_id,
      ),
      campaign_trigger_keyword: newCampaign.campaign_trigger_keyword.map(
        (k) => ({
          ...k,
          campaign_template_id:
            toPersistedCampaignTemplateId(k.campaign_template_id) ?? undefined,
        }),
      ),
      campaign_trigger_message: newCampaign.campaign_trigger_message.map(
        (m) => ({
          ...m,
          campaign_template_id:
            toPersistedCampaignTemplateId(m.campaign_template_id) ?? undefined,
        }),
      ),
      message_status: 'Draft',
      campaign_status: 'Inactive',
      clinic_id: selectedClinic?.clinic_id,
    })
    setStep(4)
  }

  const handleConfirmSave = (): void => {
    if (emailSendBlocked) {
      showToast(
        createFailedToast(
          'Google email',
          'Connect a Google mailbox under Settings > Communications > Email before sending.',
        ),
      )
      return
    }
    const formData: ICampaignRequest = {
      ...newCampaign,
      campaign_template_id: toPersistedCampaignTemplateId(
        newCampaign.campaign_template_id,
      ),
      campaign_status: 'Active',
      message_status:
        newCampaign.sending_option === 'Immediately' ? 'Sent' : 'Scheduled',
      campaign_trigger_keyword: newCampaign.campaign_trigger_keyword.map(
        (k) => ({
          ...k,
          campaign_template_id:
            toPersistedCampaignTemplateId(k.campaign_template_id) ?? undefined,
        }),
      ),
      campaign_trigger_message: newCampaign.campaign_trigger_message.map(
        (m) => ({
          ...m,
          campaign_template_id:
            toPersistedCampaignTemplateId(m.campaign_template_id) ?? undefined,
        }),
      ),
    }
    setIsDraft(false)
    handleSave(formData)
    setStep(step + 1)
  }

  const handleChange = (
    field: string,
    value:
      | string
      | Date
      | number
      | boolean
      | string[]
      | ICampaignRecipient[]
      | ICampaignTriggerMessage[]
      | ICampaignTriggerKeyword[]
      | null,
  ): void => {
    setNewCampaign((prev) => {
      const newData = { ...prev, [field]: value }

      if (field === 'campaign_template_id' && value === '') {
        newData.campaign_template = undefined
      }

      return newData
    })

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors }
      if (value) {
        delete updatedErrors[field]
        if (['sending_option', 'recurring_interval'].includes(field)) {
          delete updatedErrors.schedule_date
          delete updatedErrors.schedule_time
          delete updatedErrors.recurring_interval
          delete updatedErrors.recurring_days
          delete updatedErrors.recurring_duration
          delete updatedErrors.recurring_time
        }
      }
      return updatedErrors
    })
  }
  useEffect(() => {
    dispatch(fetchAllContacts())
  }, [selectedClinic])

  const getSectionComponent = (): React.ReactElement => {
    switch (step) {
      case 0:
        return (
          <CampaignInfo
            campaignData={newCampaign}
            handleChange={handleChange}
            validationErrors={validationErrors}
            isReactivateCampaign={method === 'reactivate'}
            googleMailboxConnected={googleMailboxConnected}
            emailSettingsPath={emailSettingsPath}
          />
        )
      case 1:
        return (
          <Scheduling
            campaignData={newCampaign}
            handleChange={handleChange}
            validationErrors={validationErrors}
          />
        )
      case 2:
        return (
          <Triggers
            campaignData={newCampaign}
            handleChange={handleChange}
            previewMessage={previewMessage}
            showTimePointModal={showTimePointModal}
            showActionTriggerModal={showActionTriggerModal}
            setShowTimePointModal={setShowTimePointModal}
            setShowActionTriggerModal={setShowActionTriggerModal}
            setPreviewMessage={setPreviewMessage}
          />
        )
      case 3:
        return (
          <Review campaignData={newCampaign} handleEdit={handleEditButton} />
        )
      case 4:
        return <div className="mt-2 appt-modal" />
      default:
        return <></>
    }
  }

  useEffect(() => {
    const tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]'),
    )
    const tooltips = tooltipTriggerList.map(
      (el) => new window.bootstrap.Tooltip(el),
    )

    return () => {
      tooltips.forEach((t) => t.dispose())
    }
  }, [step])

  return (
    <>
      {(showTimePointModal || showActionTriggerModal || previewMessage) && (
        <div
          className={`fade modal-backdrop show at-custom-backdrop ${
            previewMessage ? 'z-1064' : 'z-1059'
          }`}
        />
      )}
      <Modal
        show={show}
        onHide={onModalClose}
        size="lg"
        id="add-campaign-modal"
        className="add-campaign-modal"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {method === 'reactivate'
              ? 'Rerun'
              : initialCampaign?.campaign_id
                ? 'Edit'
                : 'Add'}{' '}
            campaign
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {step !== 4 && <Stepper steps={steps} currentStep={step} />}
          <div key={step}>{getSectionComponent()}</div>
        </Modal.Body>

        {step !== 4 && (
          <Modal.Footer className="d-inline-block">
            <Row md={12}>
              <Col md={4} className="text-start ps-0">
                {step !== 4 && (
                  <Button
                    size="md"
                    className="button-link"
                    onClick={handleSaveDraft}
                    disabled={newCampaign.name.trim() === ''}
                    data-bs-toggle={
                      newCampaign.name.trim() === '' ? 'tooltip' : undefined
                    }
                    title={
                      newCampaign.name.trim() === ''
                        ? 'Campaign name is required'
                        : undefined
                    }
                  >
                    Save as draft
                  </Button>
                )}
              </Col>
              <Col md={8} className="text-end pe-0">
                {step === 0 && (
                  <>
                    <Button
                      size="md"
                      className="button-secondary left-button"
                      onClick={onModalClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="md"
                      className="button-primary"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  </>
                )}
                {/* Steps between 1 and Review: Show "Previous" and "Next" */}
                {step > 0 && step < steps.length - 1 && (
                  <>
                    <Button
                      size="md"
                      className="button-secondary left-button"
                      onClick={handlePrevious}
                    >
                      Previous
                    </Button>
                    <Button
                      size="md"
                      className="button-primary"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  </>
                )}
                {step === steps.length - 1 ? (
                  <>
                    <Button
                      size="md"
                      className="button-secondary left-button"
                      onClick={handlePrevious}
                      disabled={loading.add}
                    >
                      Previous
                    </Button>
                    <Button
                      size="md"
                      className="button-primary"
                      onClick={handleConfirmSave}
                      disabled={loading.add || emailSendBlocked}
                    >
                      Confirm
                    </Button>
                  </>
                ) : null}
              </Col>
            </Row>
          </Modal.Footer>
        )}
      </Modal>
    </>
  )
}

export default AddCampaignModal
